import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  Snackbar,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  People,
  Policy as PolicyIcon,
  Assignment,
  Description,
  TrendingUp,
  Warning,
  CheckCircle,
  Schedule,
  AttachMoney,
  Approval,
  Close,
  Visibility,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { dashboardAPI, policiesAPI, claimsAPI, documentsAPI, underwritingDecisionsAPI, surveyReportsAPI } from 'services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import MUICharts from 'shared/components/MUICharts';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Set initial tab based on route
  const getInitialTab = () => {
    const path = location.pathname;
    if (path === '/admin/policies') return 0; // Pending Policies
    if (path === '/admin/claims') return 1; // Pending Claims
    if (path === '/admin/documents') return 2; // Pending Documents
    return 0;
  };
  
  const [tabValue, setTabValue] = useState(getInitialTab());
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('30d');
  
  // Policy Management States
  const [policies, setPolicies] = useState([]);
  const [pendingPolicies, setPendingPolicies] = useState([]);
  const [policyDialog, setPolicyDialog] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyAction, setPolicyAction] = useState('');
  const [policyNotes, setPolicyNotes] = useState('');
  
  // Claim Management States
  const [claims, setClaims] = useState([]);
  const [pendingClaims, setPendingClaims] = useState([]);
  const [claimDialog, setClaimDialog] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [claimAction, setClaimAction] = useState('');
  const [claimNotes, setClaimNotes] = useState('');
  const [claimSurveyReports, setClaimSurveyReports] = useState({}); // Map of claim_id -> surveyReport
  
  // Document Verification States
  const [documents, setDocuments] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [documentDialog, setDocumentDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentAction, setDocumentAction] = useState('');
  const [documentComments, setDocumentComments] = useState('');
  
  // Notification States
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Underwriting Decision States
  const [underwritingDialog, setUnderwritingDialog] = useState(false);
  const [selectedPolicyForUnderwriting, setSelectedPolicyForUnderwriting] = useState(null);
  const [existingUnderwritingDecision, setExistingUnderwritingDecision] = useState(null);
  const [underwritingFormData, setUnderwritingFormData] = useState({
    decision_status: 'PENDING',
    decision_date: new Date().toISOString().split('T')[0],
    base_premium: '',
    premium_adjustment_percent: '',
    adjusted_premium: '',
    coverage_period_adjusted: false,
    original_end_date: '',
    adjusted_end_date: '',
    conditions: '',
    risk_factors: '',
    risk_score: '',
    rejection_reason: '',
    decision_document_path: '',
    notes: '',
  });

  // Survey Report States
  const [surveyReportDialog, setSurveyReportDialog] = useState(false);
  const [selectedClaimForSurvey, setSelectedClaimForSurvey] = useState(null);
  const [existingSurveyReport, setExistingSurveyReport] = useState(null);
  const [surveyReportFormData, setSurveyReportFormData] = useState({
    survey_date: new Date().toISOString().split('T')[0],
    survey_status: 'PENDING',
    verified_mortality_percentage: '',
    verified_mortality_count: '',
    verified_survival_count: '',
    cause_of_loss: '',
    cause_verified: false,
    pond_condition: '',
    water_quality_parameters: '',
    disease_evidence: '',
    management_practices_assessed: '',
    salvage_available: false,
    salvage_quantity: '',
    salvage_market_value: '',
    salvage_deduction_percent: '',
    fraud_indicators: '',
    photos_taken: false,
    photo_paths: '',
    lab_reports_attached: false,
    report_file_path: '',
    recommendations: '',
    notes: '',
  });
  
  // Statistics
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalFarms: 0,
    totalPonds: 0,
    totalPolicies: 0,
    activePolicies: 0,
    pendingPolicies: 0,
    totalClaims: 0,
    pendingClaims: 0,
    approvedClaims: 0,
    rejectedClaims: 0,
    totalDocuments: 0,
    pendingDocuments: 0,
    verifiedDocuments: 0,
    rejectedDocuments: 0,
    totalPremiumAmount: 0,
    totalClaimAmount: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use optimized bulk data endpoint for better performance
      const [dashboardResponse, bulkDataResponse] = await Promise.all([
        dashboardAPI.getAdminDashboard(),
        dashboardAPI.getAdminBulkData()
      ]);

      // Process data from bulk response
      const dashboardStats = dashboardResponse.data;
      const bulkData = bulkDataResponse.data;
      
      const allPolicies = bulkData.policies;
      const allClaims = bulkData.claims;
      const allFarms = bulkData.farms;
      const allDocuments = bulkData.documents;
      
      // Calculate total ponds from farms data
      const ponds = allFarms.flatMap(farm => farm.ponds || []);

      // Filter pending items
      const pendingPoliciesList = allPolicies.filter(policy => policy.status === 'pending');
      const pendingClaimsList = allClaims.filter(claim => claim.status === 'pending');
      const pendingDocumentsList = allDocuments.filter(doc => doc.status === 'pending');

      // Calculate statistics
      const calculatedStats = {
        totalUsers: dashboardStats.total_farmers || 0,
        totalFarms: allFarms.length,
        totalPonds: ponds.length,
        totalPolicies: allPolicies.length,
        activePolicies: dashboardStats.active_policies || 0,
        pendingPolicies: pendingPoliciesList.length,
        totalClaims: allClaims.length,
        pendingClaims: dashboardStats.pending_claims || 0,
        approvedClaims: dashboardStats.approved_claims || 0,
        rejectedClaims: allClaims.filter(c => c.status === 'rejected').length,
        totalDocuments: allDocuments.length,
        pendingDocuments: pendingDocumentsList.length,
        verifiedDocuments: allDocuments.filter(d => d.status === 'verified').length,
        rejectedDocuments: allDocuments.filter(d => d.status === 'rejected').length,
        totalPremiumAmount: dashboardStats.total_premium_collected || 0,
        totalClaimAmount: dashboardStats.total_claims_paid || 0,
      };

      setDashboardData(dashboardStats);
      setPolicies(allPolicies);
      setPendingPolicies(pendingPoliciesList);
      setClaims(allClaims);
      setPendingClaims(pendingClaimsList);
      setDocuments(allDocuments);
      setPendingDocuments(pendingDocumentsList);
      setStats(calculatedStats);
      
      // Load survey reports for all claims
      const surveyReportsMap = {};
      for (const claim of allClaims) {
        try {
          const surveyResponse = await surveyReportsAPI.getByClaim(claim.id);
          if (surveyResponse.data) {
            surveyReportsMap[claim.id] = surveyResponse.data;
          }
        } catch (err) {
          // No survey report for this claim - that's okay
          console.log(`No survey report found for claim ${claim.id}`);
        }
      }
      setClaimSurveyReports(surveyReportsMap);

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      
      // Enhanced error handling for enterprise-level application
      let errorMessage = 'Failed to load dashboard data. Please try again.';
      
      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please check your connection and try again.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.';
        // Redirect to login will be handled by the API interceptor
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. You do not have permission to view this data.';
      } else if (!err.response) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
    setSnackbar({ open: true, message: 'Dashboard refreshed successfully', severity: 'success' });
  };

  const handlePolicyAction = async () => {
    try {
      if (!selectedPolicy || !policyAction) return;

      // For Plan C policies, check if underwriting decision exists
      const isPlanC = selectedPolicy.plan === 'C' || selectedPolicy.plan === 'PLAN_C';
      if (isPlanC && policyAction === 'active') {
        try {
          const decisionResponse = await underwritingDecisionsAPI.getByPolicy(selectedPolicy.id);
          const decision = decisionResponse.data;
          if (!decision || (decision.decision_status !== 'APPROVED' && decision.decision_status !== 'APPROVED_WITH_CONDITIONS')) {
            setSnackbar({ 
              open: true, 
              message: 'Plan C policies require an approved underwriting decision before activation. Please create an underwriting decision first.', 
              severity: 'warning' 
            });
            setPolicyDialog(false);
            return;
          }
        } catch (err) {
          // No underwriting decision found
          setSnackbar({ 
            open: true, 
            message: 'Plan C policies require an underwriting decision before activation. Please create an underwriting decision first.', 
            severity: 'warning' 
          });
          setPolicyDialog(false);
          return;
        }
      }

      await policiesAPI.approve(selectedPolicy.id, {
        status: policyAction,
        notes: policyNotes
      });

      // Refresh data
      await loadDashboardData();
      
      // Close dialog and reset
      setPolicyDialog(false);
      setSelectedPolicy(null);
      setPolicyAction('');
      setPolicyNotes('');
      
      setSnackbar({ 
        open: true, 
        message: `Policy ${policyAction === 'active' ? 'approved' : 'rejected'} successfully`, 
        severity: 'success' 
      });

    } catch (err) {
      console.error('Error updating policy:', err);
      setSnackbar({ open: true, message: 'Failed to update policy', severity: 'error' });
    }
  };

  const handleSubmitUnderwritingDecision = async () => {
    try {
      if (!selectedPolicyForUnderwriting) return;

      if (!underwritingFormData.decision_status || !underwritingFormData.decision_date) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required fields (Decision Status and Decision Date)',
          severity: 'error'
        });
        return;
      }

      const submitData = {
        decision_status: underwritingFormData.decision_status,
        decision_date: underwritingFormData.decision_date,
        base_premium: underwritingFormData.base_premium ? parseFloat(underwritingFormData.base_premium) : null,
        premium_adjustment_percent: underwritingFormData.premium_adjustment_percent ? parseFloat(underwritingFormData.premium_adjustment_percent) : null,
        adjusted_premium: underwritingFormData.adjusted_premium ? parseFloat(underwritingFormData.adjusted_premium) : null,
        coverage_period_adjusted: underwritingFormData.coverage_period_adjusted,
        original_end_date: underwritingFormData.original_end_date || null,
        adjusted_end_date: underwritingFormData.adjusted_end_date || null,
        conditions: underwritingFormData.conditions || null,
        risk_factors: underwritingFormData.risk_factors || null,
        risk_score: underwritingFormData.risk_score || null,
        rejection_reason: underwritingFormData.rejection_reason || null,
        decision_document_path: underwritingFormData.decision_document_path || null,
        notes: underwritingFormData.notes || null,
      };

      if (existingUnderwritingDecision) {
        // Update existing decision (if API supports it)
        await underwritingDecisionsAPI.update(selectedPolicyForUnderwriting.id, existingUnderwritingDecision.id, submitData);
        setSnackbar({ open: true, message: 'Underwriting decision updated successfully', severity: 'success' });
      } else {
        // Create new decision
        await underwritingDecisionsAPI.create(selectedPolicyForUnderwriting.id, submitData);
        setSnackbar({ open: true, message: 'Underwriting decision created successfully', severity: 'success' });
      }

      handleCloseUnderwritingDialog();
      await loadDashboardData();
    } catch (err) {
      console.error('Error saving underwriting decision:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to save underwriting decision. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleCloseUnderwritingDialog = () => {
    setUnderwritingDialog(false);
    setSelectedPolicyForUnderwriting(null);
    setExistingUnderwritingDecision(null);
    setUnderwritingFormData({
      decision_status: 'PENDING',
      decision_date: new Date().toISOString().split('T')[0],
      base_premium: '',
      premium_adjustment_percent: '',
      adjusted_premium: '',
      coverage_period_adjusted: false,
      original_end_date: '',
      adjusted_end_date: '',
      conditions: '',
      risk_factors: '',
      risk_score: '',
      rejection_reason: '',
      decision_document_path: '',
      notes: '',
    });
  };

  const handleClaimAction = async () => {
    try {
      if (!selectedClaim || !claimAction) return;

      // BUSINESS RULE: Claims cannot be set to PAID without a completed SurveyReport
      if (claimAction === 'paid') {
        try {
          const surveyResponse = await surveyReportsAPI.getByClaim(selectedClaim.id);
          const surveyReport = surveyResponse.data;
          if (!surveyReport || surveyReport.survey_status !== 'COMPLETED') {
            setSnackbar({ 
              open: true, 
              message: 'Claims cannot be paid without a completed survey report. Please create and complete a survey report first.', 
              severity: 'warning' 
            });
            setClaimDialog(false);
            return;
          }
        } catch (err) {
          // No survey report found
          setSnackbar({ 
            open: true, 
            message: 'Claims cannot be paid without a completed survey report. Please create a survey report first.', 
            severity: 'warning' 
          });
          setClaimDialog(false);
          return;
        }
      }

      await claimsAPI.process(selectedClaim.id, {
        status: claimAction,
        notes: claimNotes,
        processed_by: user.id
      });

      // Refresh data
      await loadDashboardData();
      
      // Close dialog and reset
      setClaimDialog(false);
      setSelectedClaim(null);
      setClaimAction('');
      setClaimNotes('');
      
      setSnackbar({ 
        open: true, 
        message: `Claim ${claimAction} successfully`, 
        severity: 'success' 
      });

    } catch (err) {
      console.error('Error processing claim:', err);
      setSnackbar({ open: true, message: 'Failed to process claim', severity: 'error' });
    }
  };

  const handleOpenSurveyReportDialog = async (claim) => {
    setSelectedClaimForSurvey(claim);
    try {
      const response = await surveyReportsAPI.getByClaim(claim.id);
      setExistingSurveyReport(response.data);
      if (response.data) {
        setSurveyReportFormData({
          survey_date: response.data.survey_date ? response.data.survey_date.split('T')[0] : new Date().toISOString().split('T')[0],
          survey_status: response.data.survey_status || 'PENDING',
          verified_mortality_percentage: response.data.verified_mortality_percentage || '',
          verified_mortality_count: response.data.verified_mortality_count || '',
          verified_survival_count: response.data.verified_survival_count || '',
          cause_of_loss: response.data.cause_of_loss || '',
          cause_verified: response.data.cause_verified || false,
          pond_condition: response.data.pond_condition || '',
          water_quality_parameters: response.data.water_quality_parameters || '',
          disease_evidence: response.data.disease_evidence || '',
          management_practices_assessed: response.data.management_practices_assessed || '',
          salvage_available: response.data.salvage_available || false,
          salvage_quantity: response.data.salvage_quantity || '',
          salvage_market_value: response.data.salvage_market_value || '',
          salvage_deduction_percent: response.data.salvage_deduction_percent || '',
          fraud_indicators: response.data.fraud_indicators || '',
          photos_taken: response.data.photos_taken || false,
          photo_paths: response.data.photo_paths || '',
          lab_reports_attached: response.data.lab_reports_attached || false,
          report_file_path: response.data.report_file_path || '',
          recommendations: response.data.recommendations || '',
          notes: response.data.notes || '',
        });
      }
    } catch (err) {
      // No existing survey report, start fresh
      setExistingSurveyReport(null);
      setSurveyReportFormData({
        survey_date: new Date().toISOString().split('T')[0],
        survey_status: 'PENDING',
        verified_mortality_percentage: '',
        verified_mortality_count: '',
        verified_survival_count: '',
        cause_of_loss: '',
        cause_verified: false,
        pond_condition: '',
        water_quality_parameters: '',
        disease_evidence: '',
        management_practices_assessed: '',
        salvage_available: false,
        salvage_quantity: '',
        salvage_market_value: '',
        salvage_deduction_percent: '',
        fraud_indicators: '',
        photos_taken: false,
        photo_paths: '',
        lab_reports_attached: false,
        report_file_path: '',
        recommendations: '',
        notes: '',
      });
    }
    setSurveyReportDialog(true);
  };

  const handleCloseSurveyReportDialog = () => {
    setSurveyReportDialog(false);
    setSelectedClaimForSurvey(null);
    setExistingSurveyReport(null);
    setSurveyReportFormData({
      survey_date: new Date().toISOString().split('T')[0],
      survey_status: 'PENDING',
      verified_mortality_percentage: '',
      verified_mortality_count: '',
      verified_survival_count: '',
      cause_of_loss: '',
      cause_verified: false,
      pond_condition: '',
      water_quality_parameters: '',
      disease_evidence: '',
      management_practices_assessed: '',
      salvage_available: false,
      salvage_quantity: '',
      salvage_market_value: '',
      salvage_deduction_percent: '',
      fraud_indicators: '',
      photos_taken: false,
      photo_paths: '',
      lab_reports_attached: false,
      report_file_path: '',
      recommendations: '',
      notes: '',
    });
  };

  const handleSubmitSurveyReport = async () => {
    try {
      if (!selectedClaimForSurvey) return;

      if (!surveyReportFormData.survey_date || !surveyReportFormData.cause_of_loss) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required fields (Survey Date and Cause of Loss)',
          severity: 'error'
        });
        return;
      }

      if (!surveyReportFormData.verified_mortality_percentage || parseFloat(surveyReportFormData.verified_mortality_percentage) < 70) {
        setSnackbar({
          open: true,
          message: 'Verified mortality percentage must be >= 70% for claim eligibility',
          severity: 'error'
        });
        return;
      }

      const submitData = {
        survey_date: surveyReportFormData.survey_date,
        survey_status: surveyReportFormData.survey_status,
        verified_mortality_percentage: parseFloat(surveyReportFormData.verified_mortality_percentage),
        verified_mortality_count: surveyReportFormData.verified_mortality_count ? parseInt(surveyReportFormData.verified_mortality_count) : null,
        verified_survival_count: surveyReportFormData.verified_survival_count ? parseInt(surveyReportFormData.verified_survival_count) : null,
        cause_of_loss: surveyReportFormData.cause_of_loss,
        cause_verified: surveyReportFormData.cause_verified,
        pond_condition: surveyReportFormData.pond_condition || null,
        water_quality_parameters: surveyReportFormData.water_quality_parameters || null,
        disease_evidence: surveyReportFormData.disease_evidence || null,
        management_practices_assessed: surveyReportFormData.management_practices_assessed || null,
        salvage_available: surveyReportFormData.salvage_available,
        salvage_quantity: surveyReportFormData.salvage_quantity ? parseFloat(surveyReportFormData.salvage_quantity) : null,
        salvage_market_value: surveyReportFormData.salvage_market_value ? parseFloat(surveyReportFormData.salvage_market_value) : null,
        salvage_deduction_percent: surveyReportFormData.salvage_deduction_percent ? parseFloat(surveyReportFormData.salvage_deduction_percent) : null,
        fraud_indicators: surveyReportFormData.fraud_indicators || null,
        photos_taken: surveyReportFormData.photos_taken,
        photo_paths: surveyReportFormData.photo_paths || null,
        lab_reports_attached: surveyReportFormData.lab_reports_attached,
        report_file_path: surveyReportFormData.report_file_path || null,
        recommendations: surveyReportFormData.recommendations || null,
        notes: surveyReportFormData.notes || null,
      };

      if (existingSurveyReport) {
        // Update existing report (if API supports it)
        await surveyReportsAPI.update(selectedClaimForSurvey.id, existingSurveyReport.id, submitData);
        setSnackbar({ open: true, message: 'Survey report updated successfully', severity: 'success' });
      } else {
        // Create new report
        await surveyReportsAPI.create(selectedClaimForSurvey.id, submitData);
        setSnackbar({ open: true, message: 'Survey report created successfully', severity: 'success' });
      }

      handleCloseSurveyReportDialog();
      await loadDashboardData();
    } catch (err) {
      console.error('Error saving survey report:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to save survey report. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleDocumentAction = async () => {
    try {
      if (!selectedDocument || !documentAction) return;

      await documentsAPI.verify(selectedDocument.id, documentAction === 'verified', documentComments);

      // Refresh data
      await loadDashboardData();
      
      // Close dialog and reset
      setDocumentDialog(false);
      setSelectedDocument(null);
      setDocumentAction('');
      setDocumentComments('');
      
      setSnackbar({ 
        open: true, 
        message: `Document ${documentAction} successfully`, 
        severity: 'success' 
      });

    } catch (err) {
      console.error('Error verifying document:', err);
      setSnackbar({ open: true, message: 'Failed to verify document', severity: 'error' });
    }
  };

  const StatCard = ({ title, value, icon, color, trend, subtitle, onClick, clickable }) => (
    <Card 
      sx={{ 
        height: '100%', 
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? {
          transform: 'translateY(-2px)',
          boxShadow: 4,
          background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`
        } : {}
      }}
      onClick={clickable ? onClick : undefined}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="text.secondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color} fontWeight="bold">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box color={color}>
            {icon}
          </Box>
        </Box>
        {trend && (
          <Box mt={1} display="flex" alignItems="center">
            <TrendingUp fontSize="small" color="success" />
            <Typography variant="body2" color="success.main" ml={0.5}>
              {trend}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const PolicyStatusChip = ({ status }) => {
    const statusConfig = {
      pending: { color: 'warning', label: 'Pending Review' },
      active: { color: 'success', label: 'Active' },
      expired: { color: 'default', label: 'Expired' },
      cancelled: { color: 'error', label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const ClaimStatusChip = ({ status }) => {
    const statusConfig = {
      pending: { color: 'warning', label: 'Pending' },
      approved: { color: 'success', label: 'Approved' },
      rejected: { color: 'error', label: 'Rejected' },
      processing: { color: 'info', label: 'Processing' }
    };
    
    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
  return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadDashboardData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Farmers"
            value={stats.totalFarmers || stats.totalUsers}
            icon={<People fontSize="large" />}
            color="#9c27b0"
            subtitle="Registered Farmers"
            onClick={() => navigate('/admin/users')}
            clickable={true}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Policies"
            value={stats.totalPolicies}
            icon={<PolicyIcon fontSize="large" />}
            color="#1976d2"
            subtitle={`${stats.activePolicies} active`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Claims"
            value={stats.totalClaims}
            icon={<Assignment fontSize="large" />}
            color="#388e3c"
            subtitle={`${stats.pendingClaims} pending`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Premium Collected"
            value={`₹${(stats.totalPremiumAmount / 100000).toFixed(2)}L`}
            icon={<AttachMoney fontSize="large" />}
            color="#7b1fa2"
            subtitle="Total Revenue"
          />
        </Grid>
      </Grid>

      {/* Analytics Overview Section - Only show on main dashboard */}
      {location.pathname === '/admin/dashboard' && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
            📊 Analytics Overview
          </Typography>
          <Grid container spacing={3}>
            <MUICharts 
              userData={{
                totalUsers: stats.totalUsers,
                farmers: dashboardData?.user_stats?.farmers || 0,
                agents: dashboardData?.user_stats?.agents || 0,
                admins: dashboardData?.user_stats?.admins || 0
              }}
              policyData={{
                totalPolicies: stats.totalPolicies,
                activePolicies: stats.activePolicies,
                pendingPolicies: stats.pendingPolicies
              }}
              claimData={{
                totalClaims: stats.totalClaims,
                pendingClaims: stats.pendingClaims
              }}
            />
          </Grid>
        </Box>
      )}

      {/* Tabs for different sections */}
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} variant="fullWidth">
          <Tab label={`Pending Policies (${stats.pendingPolicies})`} />
          <Tab label={`Pending Claims (${stats.pendingClaims})`} />
          <Tab label={`Pending Documents (${stats.pendingDocuments})`} />
          <Tab label="All Policies" />
          <Tab label="All Claims" />
          <Tab label="All Documents" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      {tabValue === 0 && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            Policies Awaiting Approval
      </Typography>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                  <TableCell>Policy Number</TableCell>
                  <TableCell>Farmer</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Sum Insured</TableCell>
                  <TableCell>Premium</TableCell>
                  <TableCell>Applied Date</TableCell>
                      <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                {pendingPolicies.map((policy) => {
                  const isPlanC = policy.plan === 'C' || policy.plan === 'PLAN_C';
                  return (
                    <TableRow key={policy.id} hover>
                      <TableCell>{policy.policy_number}</TableCell>
                      <TableCell>{policy.farmer_name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={policy.plan || 'N/A'} 
                          color={isPlanC ? 'warning' : 'default'} 
                          size="small"
                        />
                        {isPlanC && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Requires Underwriting
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>₹{policy.sum_insured ? parseFloat(policy.sum_insured).toFixed(2) : '0.00'}</TableCell>
                      <TableCell>₹{policy.final_premium ? parseFloat(policy.final_premium).toFixed(2) : '0.00'}</TableCell>
                      <TableCell>{new Date(policy.created_at).toLocaleDateString()}</TableCell>
                      <TableCell><PolicyStatusChip status={policy.status} /></TableCell>
                      <TableCell align="center">
                        {isPlanC && (
                          <Tooltip title="Underwriting Decision">
                            <IconButton 
                              color="warning" 
                              onClick={async () => {
                                setSelectedPolicyForUnderwriting(policy);
                                // Load existing underwriting decision if any
                                try {
                                  const response = await underwritingDecisionsAPI.getByPolicy(policy.id);
                                  setExistingUnderwritingDecision(response.data);
                                  if (response.data) {
                                    setUnderwritingFormData({
                                      decision_status: response.data.decision_status || 'PENDING',
                                      decision_date: response.data.decision_date ? response.data.decision_date.split('T')[0] : new Date().toISOString().split('T')[0],
                                      base_premium: response.data.base_premium || '',
                                      premium_adjustment_percent: response.data.premium_adjustment_percent || '',
                                      adjusted_premium: response.data.adjusted_premium || '',
                                      coverage_period_adjusted: response.data.coverage_period_adjusted || false,
                                      original_end_date: response.data.original_end_date ? response.data.original_end_date.split('T')[0] : '',
                                      adjusted_end_date: response.data.adjusted_end_date ? response.data.adjusted_end_date.split('T')[0] : '',
                                      conditions: response.data.conditions || '',
                                      risk_factors: response.data.risk_factors || '',
                                      risk_score: response.data.risk_score || '',
                                      rejection_reason: response.data.rejection_reason || '',
                                      decision_document_path: response.data.decision_document_path || '',
                                      notes: response.data.notes || '',
                                    });
                                  }
                                } catch (err) {
                                  // No existing decision, start fresh
                                  setExistingUnderwritingDecision(null);
                                  setUnderwritingFormData({
                                    decision_status: 'PENDING',
                                    decision_date: new Date().toISOString().split('T')[0],
                                    base_premium: policy.final_premium || '',
                                    premium_adjustment_percent: '',
                                    adjusted_premium: '',
                                    coverage_period_adjusted: false,
                                    original_end_date: policy.policy_end_date ? policy.policy_end_date.split('T')[0] : '',
                                    adjusted_end_date: '',
                                    conditions: '',
                                    risk_factors: '',
                                    risk_score: '',
                                    rejection_reason: '',
                                    decision_document_path: '',
                                    notes: '',
                                  });
                                }
                                setUnderwritingDialog(true);
                              }}
                            >
                              <Assignment />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Approve">
                          <IconButton 
                            color="success" 
                            onClick={() => {
                              setSelectedPolicy(policy);
                              setPolicyAction('active');
                              setPolicyDialog(true);
                            }}
                            disabled={isPlanC && !existingUnderwritingDecision}
                          >
                            <Approval />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton 
                            color="error"
                            onClick={() => {
                              setSelectedPolicy(policy);
                              setPolicyAction('cancelled');
                              setPolicyDialog(true);
                            }}
                          >
                            <Close />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton color="primary">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {pendingPolicies.length === 0 && (
                    <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No pending policies</Typography>
                      </TableCell>
                    </TableRow>
                )}
                  </TableBody>
                </Table>
              </TableContainer>
        </Paper>
      )}

      {tabValue === 1 && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            Claims Awaiting Processing
              </Typography>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                  <TableCell>Claim Number</TableCell>
                  <TableCell>Policy Number</TableCell>
                  <TableCell>Claim Type</TableCell>
                  <TableCell>Estimated Loss</TableCell>
                  <TableCell>Incident Date</TableCell>
                      <TableCell>Status</TableCell>
                  <TableCell>Survey Report</TableCell>
                  <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                {pendingClaims.map((claim) => {
                  const surveyReport = claimSurveyReports[claim.id];
                  const hasCompletedSurvey = surveyReport && surveyReport.survey_status === 'COMPLETED';
                  const hasSurvey = surveyReport !== undefined;
                  
                  return (
                    <TableRow key={claim.id} hover>
                      <TableCell>{claim.claim_number}</TableCell>
                      <TableCell>{claim.policy_number || 'N/A'}</TableCell>
                      <TableCell>{claim.claim_type}</TableCell>
                      <TableCell>₹{claim.estimated_loss?.toLocaleString()}</TableCell>
                      <TableCell>{new Date(claim.incident_date).toLocaleDateString()}</TableCell>
                      <TableCell><ClaimStatusChip status={claim.status} /></TableCell>
                      <TableCell>
                        {hasSurvey ? (
                          <Chip 
                            label={surveyReport.survey_status || 'N/A'} 
                            color={hasCompletedSurvey ? 'success' : 'warning'} 
                            size="small"
                            icon={hasCompletedSurvey ? <CheckCircle /> : <Warning />}
                          />
                        ) : (
                          <Chip 
                            label="Not Created" 
                            color="error" 
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Survey Report">
                          <IconButton 
                            color="info"
                            onClick={() => handleOpenSurveyReportDialog(claim)}
                          >
                            <Description />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Approve">
                          <IconButton 
                            color="success"
                            onClick={() => {
                              setSelectedClaim(claim);
                              setClaimAction('approved');
                              setClaimDialog(true);
                            }}
                          >
                            <Approval />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={hasCompletedSurvey ? "Pay Claim" : "Pay Claim (Survey Required)"}>
                          <span>
                            <IconButton 
                              color="primary"
                              onClick={async () => {
                                // Check survey report before opening dialog
                                try {
                                  const surveyResponse = await surveyReportsAPI.getByClaim(claim.id);
                                  const survey = surveyResponse.data;
                                  if (!survey || survey.survey_status !== 'COMPLETED') {
                                    setSnackbar({ 
                                      open: true, 
                                      message: 'Survey report must be completed before payment. Please complete the survey report first.', 
                                      severity: 'warning' 
                                    });
                                    return;
                                  }
                                  setSelectedClaim(claim);
                                  setClaimAction('paid');
                                  setClaimDialog(true);
                                } catch (err) {
                                  setSnackbar({ 
                                    open: true, 
                                    message: 'Survey report is required before payment. Please create and complete a survey report first.', 
                                    severity: 'warning' 
                                  });
                                }
                              }}
                              disabled={!hasCompletedSurvey}
                              sx={{ 
                                opacity: hasCompletedSurvey ? 1 : 0.5,
                                cursor: hasCompletedSurvey ? 'pointer' : 'not-allowed'
                              }}
                            >
                              <AttachMoney />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton 
                            color="error"
                            onClick={() => {
                              setSelectedClaim(claim);
                              setClaimAction('rejected');
                              setClaimDialog(true);
                            }}
                          >
                            <Close />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton color="primary">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {pendingClaims.length === 0 && (
                    <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No pending claims</Typography>
                      </TableCell>
                    </TableRow>
                )}
                  </TableBody>
                </Table>
              </TableContainer>
        </Paper>
      )}

      {tabValue === 2 && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            Documents Awaiting Verification
          </Typography>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Document Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell>Upload Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingDocuments.map((document) => (
                  <TableRow key={document.id} hover>
                    <TableCell>{document.title}</TableCell>
                    <TableCell>{document.document_type}</TableCell>
                    <TableCell>{document.user_name || 'N/A'}</TableCell>
                    <TableCell>{new Date(document.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={document.status} 
                        color={document.status === 'pending' ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Verify">
                        <IconButton 
                          color="success"
                          onClick={() => {
                            setSelectedDocument(document);
                            setDocumentAction('verified');
                            setDocumentDialog(true);
                          }}
                        >
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton 
                          color="error"
                          onClick={() => {
                            setSelectedDocument(document);
                            setDocumentAction('rejected');
                            setDocumentDialog(true);
                          }}
                        >
                          <Close />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Details">
                        <IconButton color="primary">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {pendingDocuments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No pending documents</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tabValue === 3 && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            All Policies
          </Typography>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Policy Number</TableCell>
                  <TableCell>Farmer</TableCell>
                  <TableCell>Sum Insured</TableCell>
                  <TableCell>Premium</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {policies.map((policy) => (
                  <TableRow key={policy.id} hover>
                    <TableCell>{policy.policy_number}</TableCell>
                    <TableCell>{policy.farmer_name || 'N/A'}</TableCell>
                    <TableCell>₹{policy.sum_insured ? parseFloat(policy.sum_insured).toFixed(2) : '0.00'}</TableCell>
                    <TableCell>₹{policy.final_premium ? parseFloat(policy.final_premium).toFixed(2) : '0.00'}</TableCell>
                    <TableCell><PolicyStatusChip status={policy.status} /></TableCell>
                    <TableCell>{new Date(policy.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tabValue === 4 && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            All Claims
          </Typography>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Claim Number</TableCell>
                  <TableCell>Policy Number</TableCell>
                  <TableCell>Claim Type</TableCell>
                  <TableCell>Estimated Loss</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claims.map((claim) => (
                  <TableRow key={claim.id} hover>
                    <TableCell>{claim.claim_number}</TableCell>
                    <TableCell>{claim.policy_number || 'N/A'}</TableCell>
                    <TableCell>{claim.claim_type}</TableCell>
                    <TableCell>₹{claim.estimated_loss?.toLocaleString()}</TableCell>
                    <TableCell><ClaimStatusChip status={claim.status} /></TableCell>
                    <TableCell>{new Date(claim.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tabValue === 5 && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            All Documents
          </Typography>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Document Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Upload Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document) => (
                  <TableRow key={document.id} hover>
                    <TableCell>{document.title}</TableCell>
                    <TableCell>{document.document_type}</TableCell>
                    <TableCell>{document.user_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={document.status} 
                        color={
                          document.status === 'verified' ? 'success' : 
                          document.status === 'rejected' ? 'error' : 'warning'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(document.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Policy Action Dialog */}
      <Dialog open={policyDialog} onClose={() => setPolicyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {policyAction === 'active' ? 'Approve Policy' : 'Reject Policy'}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Policy Number: <strong>{selectedPolicy?.policy_number}</strong>
      </Typography>
          <Typography gutterBottom>
            Sum Insured: <strong>₹{selectedPolicy?.sum_insured?.toLocaleString()}</strong>
              </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Notes (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={policyNotes}
            onChange={(e) => setPolicyNotes(e.target.value)}
            placeholder="Add any comments or notes for this decision..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPolicyDialog(false)}>Cancel</Button>
          <Button 
            onClick={handlePolicyAction} 
            variant="contained"
            color={policyAction === 'active' ? 'success' : 'error'}
          >
            {policyAction === 'active' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Claim Action Dialog */}
      <Dialog open={claimDialog} onClose={() => setClaimDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {claimAction === 'approved' ? 'Approve Claim' : 
           claimAction === 'paid' ? 'Pay Claim' : 'Reject Claim'}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Claim Number: <strong>{selectedClaim?.claim_number}</strong>
          </Typography>
          <Typography gutterBottom>
            Estimated Loss: <strong>₹{selectedClaim?.estimated_loss?.toLocaleString()}</strong>
          </Typography>
          
          {/* Survey Report Status */}
          {selectedClaim && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Survey Report Status:
              </Typography>
              {(() => {
                const surveyReport = claimSurveyReports[selectedClaim.id];
                if (!surveyReport) {
                  return (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>No Survey Report Found</strong>
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        A survey report is required before processing this claim. Please create a survey report first.
                      </Typography>
                    </Alert>
                  );
                }
                const isCompleted = surveyReport.survey_status === 'COMPLETED';
                return (
                  <Box>
                    <Chip 
                      label={surveyReport.survey_status || 'N/A'} 
                      color={isCompleted ? 'success' : 'warning'} 
                      size="small"
                      icon={isCompleted ? <CheckCircle /> : <Warning />}
                      sx={{ mb: 1 }}
                    />
                    {surveyReport.verified_mortality_percentage !== undefined && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Verified Mortality: <strong>{surveyReport.verified_mortality_percentage}%</strong>
                      </Typography>
                    )}
                    {surveyReport.survey_date && (
                      <Typography variant="body2">
                        Survey Date: <strong>{new Date(surveyReport.survey_date).toLocaleDateString()}</strong>
                      </Typography>
                    )}
                    {claimAction === 'paid' && !isCompleted && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          Survey report must be <strong>COMPLETED</strong> before payment can be processed.
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                );
              })()}
            </Box>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Processing Notes"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={claimNotes}
            onChange={(e) => setClaimNotes(e.target.value)}
            placeholder="Add processing notes, approved amount, etc..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleClaimAction} 
            variant="contained"
            color={
              claimAction === 'approved' ? 'success' : 
              claimAction === 'paid' ? 'primary' : 'error'
            }
            disabled={
              !claimNotes.trim() || 
              (claimAction === 'paid' && 
               selectedClaim && 
               (!claimSurveyReports[selectedClaim.id] || 
                claimSurveyReports[selectedClaim.id].survey_status !== 'COMPLETED'))
            }
          >
            {claimAction === 'approved' ? 'Approve' : 
             claimAction === 'paid' ? 'Pay Claim' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Action Dialog */}
      <Dialog open={documentDialog} onClose={() => setDocumentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {documentAction === 'verified' ? 'Verify Document' : 'Reject Document'}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Document Title: <strong>{selectedDocument?.title}</strong>
          </Typography>
          <Typography gutterBottom>
            Document Type: <strong>{selectedDocument?.document_type}</strong>
          </Typography>
          <Typography gutterBottom>
            Uploaded By: <strong>{selectedDocument?.user_name || 'N/A'}</strong>
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Verification Comments"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={documentComments}
            onChange={(e) => setDocumentComments(e.target.value)}
            placeholder="Add verification comments, reasons for rejection, etc..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDocumentAction} 
            variant="contained"
            color={documentAction === 'verified' ? 'success' : 'error'}
            disabled={!documentComments.trim()}
          >
            {documentAction === 'verified' ? 'Verify' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Underwriting Decision Dialog */}
      <Dialog 
        open={underwritingDialog} 
        onClose={handleCloseUnderwritingDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {existingUnderwritingDecision ? 'Edit Underwriting Decision' : 'Create Underwriting Decision'}
          <IconButton
            aria-label="close"
            onClick={handleCloseUnderwritingDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Policy:</strong> {selectedPolicyForUnderwriting?.policy_number || 'N/A'} (Plan C)
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  <strong>Note:</strong> Plan C policies require an approved underwriting decision before activation.
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Decision Status</InputLabel>
                <Select
                  value={underwritingFormData.decision_status}
                  label="Decision Status"
                  onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, decision_status: e.target.value })}
                >
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="APPROVED_WITH_CONDITIONS">Approved with Conditions</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                  <MenuItem value="MODIFIED">Modified</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Decision Date"
                type="date"
                value={underwritingFormData.decision_date || ''}
                onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, decision_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Base Premium"
                type="number"
                value={underwritingFormData.base_premium || ''}
                onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, base_premium: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Original premium amount"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Premium Adjustment (%)"
                type="number"
                value={underwritingFormData.premium_adjustment_percent || ''}
                onChange={(e) => {
                  const adjustment = parseFloat(e.target.value) || 0;
                  const basePremium = parseFloat(underwritingFormData.base_premium) || 0;
                  const adjustedPremium = basePremium * (1 + adjustment / 100);
                  setUnderwritingFormData({ 
                    ...underwritingFormData, 
                    premium_adjustment_percent: e.target.value,
                    adjusted_premium: adjustedPremium > 0 ? adjustedPremium.toFixed(2) : ''
                  });
                }}
                inputProps={{ min: -100, max: 100, step: 0.01 }}
                helperText="Adjustment percentage"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Adjusted Premium"
                type="number"
                value={underwritingFormData.adjusted_premium || ''}
                onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, adjusted_premium: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Final premium after adjustment"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Original End Date"
                type="date"
                value={underwritingFormData.original_end_date || ''}
                onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, original_end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Adjusted End Date"
                type="date"
                value={underwritingFormData.adjusted_end_date || ''}
                onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, adjusted_end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                disabled={!underwritingFormData.coverage_period_adjusted}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={underwritingFormData.coverage_period_adjusted}
                    onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, coverage_period_adjusted: e.target.checked })}
                  />
                }
                label="Coverage Period Adjusted"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Risk Score"
                type="number"
                value={underwritingFormData.risk_score || ''}
                onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, risk_score: e.target.value })}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                helperText="Risk assessment score (0-100)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Risk Factors"
                value={underwritingFormData.risk_factors || ''}
                onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, risk_factors: e.target.value })}
                multiline
                rows={2}
                placeholder="List identified risk factors"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Conditions"
                value={underwritingFormData.conditions || ''}
                onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, conditions: e.target.value })}
                multiline
                rows={2}
                placeholder="Special conditions or requirements"
              />
            </Grid>

            {underwritingFormData.decision_status === 'REJECTED' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Rejection Reason"
                  value={underwritingFormData.rejection_reason || ''}
                  onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, rejection_reason: e.target.value })}
                  multiline
                  rows={2}
                  required
                  placeholder="Explain why the policy was rejected"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Decision Document Path/URL"
                value={underwritingFormData.decision_document_path || ''}
                onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, decision_document_path: e.target.value })}
                placeholder="Path or URL to decision document"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={underwritingFormData.notes || ''}
                onChange={(e) => setUnderwritingFormData({ ...underwritingFormData, notes: e.target.value })}
                multiline
                rows={3}
                placeholder="Additional notes about the underwriting decision"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnderwritingDialog} type="button">Cancel</Button>
          <Button 
            onClick={handleSubmitUnderwritingDecision} 
            variant="contained" 
            disabled={!underwritingFormData.decision_status || !underwritingFormData.decision_date}
            type="button"
            sx={{ minWidth: 100 }}
          >
            {existingUnderwritingDecision ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Survey Report Dialog */}
      <Dialog 
        open={surveyReportDialog} 
        onClose={handleCloseSurveyReportDialog} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          {existingSurveyReport ? 'Edit Survey Report' : 'Create Survey Report'}
          <IconButton
            aria-label="close"
            onClick={handleCloseSurveyReportDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Claim:</strong> {selectedClaimForSurvey?.claim_number || 'N/A'}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  <strong>Note:</strong> Verified mortality percentage must be >= 70% for claim eligibility.
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Survey Date"
                type="date"
                value={surveyReportFormData.survey_date || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, survey_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Survey Status</InputLabel>
                <Select
                  value={surveyReportFormData.survey_status}
                  label="Survey Status"
                  onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, survey_status: e.target.value })}
                >
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Verified Mortality %"
                type="number"
                value={surveyReportFormData.verified_mortality_percentage || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, verified_mortality_percentage: e.target.value })}
                inputProps={{ min: 70, max: 100, step: 0.01 }}
                required
                error={surveyReportFormData.verified_mortality_percentage && parseFloat(surveyReportFormData.verified_mortality_percentage) < 70}
                helperText={surveyReportFormData.verified_mortality_percentage && parseFloat(surveyReportFormData.verified_mortality_percentage) < 70 ? 'Must be >= 70% for claim eligibility' : 'Required: >= 70%'}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Verified Mortality Count"
                type="number"
                value={surveyReportFormData.verified_mortality_count || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, verified_mortality_count: e.target.value })}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Verified Survival Count"
                type="number"
                value={surveyReportFormData.verified_survival_count || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, verified_survival_count: e.target.value })}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cause of Loss"
                value={surveyReportFormData.cause_of_loss || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, cause_of_loss: e.target.value })}
                required
                placeholder="Primary cause of the loss"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={surveyReportFormData.cause_verified}
                    onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, cause_verified: e.target.checked })}
                  />
                }
                label="Cause Verified"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={surveyReportFormData.salvage_available}
                    onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, salvage_available: e.target.checked })}
                  />
                }
                label="Salvage Available"
              />
            </Grid>

            {surveyReportFormData.salvage_available && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Salvage Quantity (kg)"
                    type="number"
                    value={surveyReportFormData.salvage_quantity || ''}
                    onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, salvage_quantity: e.target.value })}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Salvage Market Value (₹/kg)"
                    type="number"
                    value={surveyReportFormData.salvage_market_value || ''}
                    onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, salvage_market_value: e.target.value })}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Salvage Deduction %"
                    type="number"
                    value={surveyReportFormData.salvage_deduction_percent || ''}
                    onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, salvage_deduction_percent: e.target.value })}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Pond Condition"
                value={surveyReportFormData.pond_condition || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, pond_condition: e.target.value })}
                multiline
                rows={2}
                placeholder="Assessment of pond condition"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Water Quality Parameters"
                value={surveyReportFormData.water_quality_parameters || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, water_quality_parameters: e.target.value })}
                multiline
                rows={2}
                placeholder="Water quality test results"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Disease Evidence"
                value={surveyReportFormData.disease_evidence || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, disease_evidence: e.target.value })}
                multiline
                rows={2}
                placeholder="Evidence of disease or other issues"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Management Practices Assessed"
                value={surveyReportFormData.management_practices_assessed || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, management_practices_assessed: e.target.value })}
                multiline
                rows={2}
                placeholder="Assessment of farm management practices"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fraud Indicators"
                value={surveyReportFormData.fraud_indicators || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, fraud_indicators: e.target.value })}
                multiline
                rows={2}
                placeholder="Any indicators of potential fraud"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={surveyReportFormData.photos_taken}
                    onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, photos_taken: e.target.checked })}
                  />
                }
                label="Photos Taken"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={surveyReportFormData.lab_reports_attached}
                    onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, lab_reports_attached: e.target.checked })}
                  />
                }
                label="Lab Reports Attached"
              />
            </Grid>

            {surveyReportFormData.photos_taken && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Photo Paths/URLs"
                  value={surveyReportFormData.photo_paths || ''}
                  onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, photo_paths: e.target.value })}
                  placeholder="Comma-separated paths or URLs to photos"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Report File Path/URL"
                value={surveyReportFormData.report_file_path || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, report_file_path: e.target.value })}
                placeholder="Path or URL to survey report document"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Recommendations"
                value={surveyReportFormData.recommendations || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, recommendations: e.target.value })}
                multiline
                rows={3}
                placeholder="Recommendations for the farmer"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={surveyReportFormData.notes || ''}
                onChange={(e) => setSurveyReportFormData({ ...surveyReportFormData, notes: e.target.value })}
                multiline
                rows={3}
                placeholder="Additional notes about the survey"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSurveyReportDialog} type="button">Cancel</Button>
          <Button 
            onClick={handleSubmitSurveyReport} 
            variant="contained" 
            disabled={!surveyReportFormData.survey_date || !surveyReportFormData.cause_of_loss || !surveyReportFormData.verified_mortality_percentage || parseFloat(surveyReportFormData.verified_mortality_percentage) < 70}
            type="button"
            sx={{ minWidth: 100 }}
          >
            {existingSurveyReport ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;